import  { useState, useEffect } from 'react';
import {
    Table, Container, Row, Col,
    Button, Badge, Dropdown,
    Form, InputGroup, Spinner, Alert,
    Card
} from 'react-bootstrap';
import Layout from '../Dashbaord/Layout.js/Layout';
import { useNavigate } from 'react-router-dom';
import UserService from '../../../services/user.services';
import {
    FaEdit, FaTrash, FaEye, FaUserPlus, FaSearch,
    FaFilter, FaSort, FaEllipsisV, FaDownload,
    FaUsers, FaUserCheck, FaUserTimes, FaUserCog,
    FaIdCard, FaPhone, FaMapMarkerAlt, FaMoneyBill,
    FaCalendarAlt, FaBuilding, FaBriefcase
} from 'react-icons/fa';
import { colors } from '../../../utils/Colors';
const UserList = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalUsers, setTotalUsers] = useState(0);
    const [stats, setStats] = useState(null);
    const [filterActive, setFilterActive] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    useEffect(() => {
        fetchUsers();
        fetchStats();
    }, [currentPage, filterActive, sortConfig]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await UserService.getAllUsers({
                page: currentPage,
                limit: 10,
                search: searchTerm,
                isActive: filterActive === 'active' ? 'true' : filterActive === 'inactive' ? 'false' : '',
                sortBy: sortConfig.key,
                sortOrder: sortConfig.direction
            });

            if (response && response.data) {
                setUsers(response.data);
                setTotalPages(response.pagination?.totalPages || 1);
                setTotalUsers(response.pagination?.total || 0);
                UserService.getTotalSimpleCounts()
            } else {
                setUsers([]);
                setTotalPages(1);
                setTotalUsers(0);
            }

            setError('');
        } catch (err) {
            setError('Failed to fetch users. Please try again.');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await UserService.getUserStats();
            if (response && response.data) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchUsers();
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this user?')) {
            try {
                await UserService.deleteUser(id);
                fetchUsers();
                fetchStats();
            } catch (err) {
                setError('Failed to delete user. Please try again.');
            }
        }
    };

    const handleSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    const getStatusBadge = (isActive, resigned) => {
        if (resigned) {
            return (
                <Badge bg="danger" className="d-flex align-items-center gap-1 px-2 py-1">
                    <FaUserTimes size={10} />
                    Resigned
                </Badge>
            );
        }
        return isActive ? (
            <Badge bg="success" className="d-flex align-items-center gap-1 px-2 py-1">
                <FaUserCheck size={10} />
                Active
            </Badge>
        ) : (
            <Badge bg="warning" className="d-flex align-items-center gap-1 px-2 py-1">
                <FaUserTimes size={10} />
                Inactive
            </Badge>
        );
    };

    const getRoleBadge = (role) => {
        const roleColors = {
            'admin': { bg: 'dark', icon: FaUserCog },
            'manager': { bg: 'primary', icon: FaBriefcase },
            'employee': { bg: 'info', icon: FaUsers },
            'staff': { bg: 'secondary', icon: FaUsers },
            'supervisor': { bg: 'warning', icon: FaUserCog }
        };

        const roleConfig = roleColors[role?.toLowerCase()] || { bg: 'secondary', icon: FaUsers };
        const Icon = roleConfig.icon;

        return (
            <Badge bg={roleConfig.bg} className="d-flex align-items-center gap-1 px-2 py-1">
                <Icon size={10} />
                {role}
            </Badge>
        );
    };

    const formatPhoneNumber = (phone) => {
        if (!phone) return 'N/A';
        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned.startsWith('92')) {
            return `${cleaned.substring(0, 4)}-${cleaned.substring(4, 7)}-${cleaned.substring(7)}`;
        }
        return phone;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateExperience = (joinedDate) => {
        if (!joinedDate) return 'N/A';
        const joined = new Date(joinedDate);
        const now = new Date();
        const diffTime = Math.abs(now - joined);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 30) return `${diffDays} days`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)} months`;
        return `${Math.floor(diffDays / 365)} years`;
    };

    const exportToCSV = () => {
        
        const headers = ['Name', 'CNIC', 'Phone', 'Role', 'Status', 'Salary', 'Joined Date'];
        const csvContent = [
            headers.join(','),
            ...users.map(user => [
                `"${user.name}"`,
                `"${user.cnic}"`,
                `"${user.phoneNumber}"`,
                `"${user.role}"`,
                `"${user.isActive ? 'Active' : 'Inactive'}"`,
                `"${user.salary?.netSalary || 0}"`,
                `"${formatDate(user.joinedDate)}"`
            ].join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `employees-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
    };

    return (
        <Layout>
            <Container fluid className="py-4">
                
                <Row className="mb-4 align-items-center p-4 rounded-3" style={{ backgroundColor : colors.TABLE_HEADERS}}>
                    <Col md={6}>
                        <div>
                            <h2 className="fw-bold mb-2 text-white">Employee Management</h2>
                            <p className="text-white-50 mb-0">
                                <FaUsers className="me-2" />
                                Manage all employee records and information
                            </p>
                        </div>
                    </Col>

                    <Col md={6} className="text-md-end">
                        <div className="d-flex gap-2 justify-content-md-end">
                            <Button
                                variant="outline-light"
                                onClick={exportToCSV}
                                className="d-flex align-items-center gap-2"
                            >
                                <FaDownload />
                                Export
                            </Button>
                            <Button
                                variant="light"
                                onClick={() => navigate('/admin/dashboard/create-user')}
                                className="d-flex align-items-center gap-2 px-4"
                            >
                                <FaUserPlus />
                                Add Employee
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* Stats Cards */}
                <Row className="mb-4">
                    <Col md={3}>
                        <Card className="border-0 shadow-sm h-100" style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white'
                        }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h3 className="fw-bold mb-1">{totalUsers}</h3>
                                        <p className="mb-0 opacity-75">Total Employees</p>
                                    </div>
                                    <div className="bg-white bg-opacity-25 p-3 rounded-circle">
                                        <FaUsers size={24} />
                                    </div>
                                </div>
                                <div className="mt-3 small opacity-75">
                                    <FaCalendarAlt className="me-1" />
                                    Updated just now
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className="border-0 shadow-sm h-100" style={{
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white'
                        }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h3 className="fw-bold mb-1">
                                            {users.filter(u => u.isActive && !u.resigned).length}
                                        </h3>
                                        <p className="mb-0 opacity-75">Active Employees</p>
                                    </div>
                                    <div className="bg-white bg-opacity-25 p-3 rounded-circle">
                                        <FaUserCheck size={24} />
                                    </div>
                                </div>
                                <div className="mt-3 small opacity-75">
                                    <FaUserCheck className="me-1" />
                                    Currently working
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className="border-0 shadow-sm h-100" style={{
                            background: 'linear-gradient(135deg, #f46b45 0%, #eea849 100%)',
                            color: 'white'
                        }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h3 className="fw-bold mb-1">
                                            {users.filter(u => !u.isActive || u.resigned).length}
                                        </h3>
                                        <p className="mb-0 opacity-75">Inactive Employees</p>
                                    </div>
                                    <div className="bg-white bg-opacity-25 p-3 rounded-circle">
                                        <FaUserTimes size={24} />
                                    </div>
                                </div>
                                <div className="mt-3 small opacity-75">
                                    <FaUserTimes className="me-1" />
                                    Resigned/Inactive
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col md={3}>
                        <Card className="border-0 shadow-sm h-100" style={{
                            background: 'linear-gradient(135deg, #0072ff 0%, #00c6ff 100%)',
                            color: 'white'
                        }}>
                            <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h3 className="fw-bold mb-1">
                                            {users.filter(u => u.role === 'admin').length}
                                        </h3>
                                        <p className="mb-0 opacity-75">Admin Users</p>
                                    </div>
                                    <div className="bg-white bg-opacity-25 p-3 rounded-circle">
                                        <FaUserCog size={24} />
                                    </div>
                                </div>
                                <div className="mt-3 small opacity-75">
                                    <FaUserCog className="me-1" />
                                    System administrators
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Search and Filter Section */}
                <Card className="border-0 shadow-sm mb-4">
                    <Card.Body>
                        <Row className="align-items-center">
                            <Col md={8}>
                                <Form onSubmit={handleSearch}>
                                    <InputGroup className="border rounded-3 overflow-hidden">
                                        <InputGroup.Text className="bg-white border-0">
                                            <FaSearch className="text-muted" />
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="search"
                                            placeholder="Search by name, CNIC, phone, department..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="border-0 shadow-none"
                                        />
                                        <Button
                                            variant="light"
                                            type="submit"
                                            className="border-0"
                                        >
                                            Search
                                        </Button>
                                    </InputGroup>
                                </Form>
                            </Col>

                            <Col md={4}>
                                <div className="d-flex gap-2 justify-content-end">
                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-dark" className="d-flex align-items-center gap-2">
                                            <FaFilter />
                                            {filterActive === 'all' ? 'All' : filterActive === 'active' ? 'Active' : 'Inactive'}
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => setFilterActive('all')}>
                                                All Employees
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setFilterActive('active')}>
                                                Active Only
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => setFilterActive('inactive')}>
                                                Inactive Only
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>

                                    <Dropdown>
                                        <Dropdown.Toggle variant="outline-dark" className="d-flex align-items-center gap-2">
                                            <FaSort />
                                            Sort
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleSort('name')}>
                                                Name A-Z
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleSort('name', 'desc')}>
                                                Name Z-A
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleSort('joinedDate', 'desc')}>
                                                Newest First
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleSort('joinedDate', 'asc')}>
                                                Oldest First
                                            </Dropdown.Item>
                                            <Dropdown.Item onClick={() => handleSort('salary.netSalary', 'desc')}>
                                                Highest Salary
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Error Alert */}
                {error && (
                    <Alert variant="danger" onClose={() => setError('')} dismissible className="border-0 shadow-sm">
                        <div className="d-flex align-items-center">
                            <FaUserTimes className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}

                {/* Users Table */}
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-white border-0 py-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0 fw-bold text-dark">
                                <FaUsers className="me-2" />
                                Employee Records
                            </h6>
                            <div className="text-muted small">
                                Showing {((currentPage - 1) * 10) + 1} - {Math.min(currentPage * 10, totalUsers)} of {totalUsers}
                            </div>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="mt-3 text-muted">Loading employee records...</p>
                            </div>
                        ) : !users || users.length === 0 ? (
                            <div className="text-center py-5">
                                <div className="mb-4">
                                    <FaUsers className="text-muted" size={48} />
                                </div>
                                <h5 className="text-dark mb-2">No employees found</h5>
                                <p className="text-muted mb-4">Try adjusting your search criteria or add a new employee</p>
                                <Button
                                    variant="outline-primary"
                                    onClick={() => navigate('/admin/dashboard/create-user')}
                                    className="d-flex align-items-center gap-2 mx-auto"
                                >
                                    <FaUserPlus />
                                    Add New Employee
                                </Button>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                                        <tr>
                                            <th className="py-3 border-0" style={{ width: '5%' }}>#</th>
                                            <th className="py-3 border-0" style={{ width: '25%' }}>Employee Details</th>
                                            <th className="py-3 border-0" style={{ width: '20%' }}>Contact & Location</th>
                                            <th className="py-3 border-0" style={{ width: '15%' }}>Position</th>
                                            <th className="py-3 border-0" style={{ width: '15%' }}>Status & Tenure</th>
                                            <th className="py-3 border-0" style={{ width: '10%' }}>Salary</th>
                                            <th className="py-3 border-0 text-center" style={{ width: '10%' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user, index) => (
                                            <tr key={user._id} className="align-middle border-top">
                                                <td className="py-3">
                                                    <div className="text-muted small">{(currentPage - 1) * 10 + index + 1}</div>
                                                </td>

                                                <td className="py-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="position-relative me-3">
                                                            {user.picture?.url ? (
                                                                <img
                                                                    src={user.picture.url}
                                                                    alt={user.name}
                                                                    className="rounded-circle border"
                                                                    style={{
                                                                        width: '48px',
                                                                        height: '48px',
                                                                        objectFit: 'cover',
                                                                        borderColor: '#e9ecef'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <div className="rounded-circle border d-flex align-items-center justify-content-center"
                                                                    style={{
                                                                        width: '48px',
                                                                        height: '48px',
                                                                        backgroundColor: '#f8f9fa',
                                                                        borderColor: '#e9ecef'
                                                                    }}>
                                                                    <span className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                                                                        {user.name?.charAt(0).toUpperCase() || 'U'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {user.isActive && !user.resigned && (
                                                                <div className="position-absolute bottom-0 end-0 bg-success rounded-circle border border-white"
                                                                    style={{ width: '12px', height: '12px' }} />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="fw-bold text-dark mb-1">{user.name || 'No Name'}</div>
                                                            <div className="d-flex align-items-center text-muted small mb-1">
                                                                <FaIdCard className="me-1" size={12} />
                                                                {user.cnic || 'N/A'}
                                                            </div>
                                                            <div className="d-flex align-items-center text-muted small">
                                                                <FaBriefcase className="me-1" size={12} />
                                                                {user.designation || 'Employee'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>

                                                <td className="py-3">
                                                    <div className="d-flex align-items-center text-dark mb-1">
                                                        <FaPhone className="me-2 text-muted" size={14} />
                                                        {formatPhoneNumber(user.phoneNumber)}
                                                    </div>
                                                    {user.email && (
                                                        <div className="text-muted small mb-1">
                                                            {user.email}
                                                        </div>
                                                    )}
                                                    {user.address && (
                                                        <div className="d-flex align-items-center text-muted small">
                                                            <FaMapMarkerAlt className="me-1" size={12} />
                                                            <span className="text-truncate" style={{ maxWidth: '200px' }}>
                                                                {user.address}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="py-3">
                                                    <div className="mb-2">
                                                        {getRoleBadge(user.role)}
                                                    </div>
                                                    {user.categories && (
                                                        <div className="mb-1">
                                                            <Badge bg="light" text="dark" className="small fw-normal">
                                                                {user.categories}
                                                            </Badge>
                                                        </div>
                                                    )}
                                                    {user.department && (
                                                        <div className="d-flex align-items-center text-muted small">
                                                            <FaBuilding className="me-1" size={12} />
                                                            {user.department}
                                                        </div>
                                                    )}
                                                </td>

                                                <td className="py-3">
                                                    <div className="mb-2">
                                                        {getStatusBadge(user.isActive, user.resigned)}
                                                    </div>
                                                    {user.joinedDate && (
                                                        <div className="d-flex align-items-center text-muted small mb-1">
                                                            <FaCalendarAlt className="me-1" size={12} />
                                                            Joined: {formatDate(user.joinedDate)}
                                                        </div>
                                                    )}
                                                    <div className="text-muted small">
                                                        Experience: {calculateExperience(user.joinedDate)}
                                                    </div>
                                                </td>

                                                <td className="py-3">
                                                    {user.salary ? (
                                                        <>
                                                            <div className="d-flex align-items-center fw-bold text-dark mb-1">
                                                                <FaMoneyBill className="me-2 text-success" size={14} />
                                                                PKR {user.salary.toLocaleString()}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {user.salary.paymentFrequency || 'monthly'}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <Badge bg="light" text="dark" className="fw-normal">
                                                            Not Set
                                                        </Badge>
                                                    )}
                                                </td>

                                                <td className="py-3 text-center">
                                                    <div className="d-flex justify-content-center gap-1">
                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            onClick={() => navigate(`/admin/dashboard/view-update/${user._id}`)}
                                                            title="View Details"
                                                            className="border"
                                                        >
                                                            <FaEye />
                                                        </Button>

                                                        <Button
                                                            variant="light"
                                                            size="sm"
                                                            onClick={() => navigate(`/admin/dashboard/view-update/${user._id}`)}
                                                            title="Edit"
                                                            className="border text-warning"
                                                        >
                                                            <FaEdit />
                                                        </Button>

                                                        <Dropdown>
                                                            <Dropdown.Toggle
                                                                variant="light"
                                                                size="sm"
                                                                id={`dropdown-${user._id}`}
                                                                className="border"
                                                            >
                                                                <FaEllipsisV />
                                                            </Dropdown.Toggle>
                                                            <Dropdown.Menu>
                                                              
                                                                <Dropdown.Item
                                                                    onClick={() => navigate(`/admin/users/${user._id}/resign`)}
                                                                    className="d-flex align-items-center gap-2"
                                                                >
                                                                    <FaUserTimes />
                                                                    {user.resigned ? 'Unmark Resigned' : 'Mark as Resigned'}
                                                                </Dropdown.Item>
                                                                <Dropdown.Divider />
                                                               
                                                                <Dropdown.Item className="text-danger d-flex align-items-center gap-2"
                                                                    onClick={() => handleDelete(user._id)}>
                                                                    <FaTrash />
                                                                    Delete Employee
                                                                </Dropdown.Item>
                                                            </Dropdown.Menu>
                                                        </Dropdown>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>

                    {/* Pagination */}
                    {users && users.length > 0 && (
                        <Card.Footer className="bg-white border-0 py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="text-muted small">
                                    Page {currentPage} of {totalPages}
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                    >
                                        Previous
                                    </Button>

                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <Button
                                                key={pageNum}
                                                variant={currentPage === pageNum ? "primary" : "outline-dark"}
                                                size="sm"
                                                onClick={() => setCurrentPage(pageNum)}
                                                className="px-3"
                                            >
                                                {pageNum}
                                            </Button>
                                        );
                                    })}

                                    <Button
                                        variant="outline-dark"
                                        size="sm"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>
                        </Card.Footer>
                    )}
                </Card>
            </Container>
        </Layout>
    );
};

export default UserList;